/**
 * Shared drag-indicator overlay for row and column reordering.
 *
 * - `direction: 'horizontal'` → row drag: full-width line at `insertOffset` Y,
 *    ghost chip pinned to the left.
 * - `direction: 'vertical'`   → column drag: full-height line at `insertOffset` X,
 *    ghost chip pinned to the top.
 *
 * Rendered as an absolutely-positioned layer *outside* the scroll container so
 * the ghost chip is never clipped by `overflow-auto`.
 */

interface DragIndicatorProps {
    /** Pixel offset (px) for the blue insertion line. `null` = hidden. */
    insertOffset: number | null;
    /** Pixel offset (px) for the ghost chip. `null` = hidden. */
    ghostOffset: number | null;
    /** Text shown inside the ghost chip. */
    ghostLabel: string;
    /** Transition duration (ms) — 0 disables animation (large jumps). */
    transitionMs: number;
    /** `horizontal` = row drag (Y axis), `vertical` = column drag (X axis). */
    direction: 'horizontal' | 'vertical';
}

export function DragIndicators({
    insertOffset,
    ghostOffset,
    ghostLabel,
    transitionMs,
    direction,
}: DragIndicatorProps) {
    if (insertOffset === null && ghostOffset === null) return null;

    const isRow = direction === 'horizontal';

    return (
        <>
            {/* Blue insertion line */}
            {insertOffset !== null && (
                <div
                    className={
                        isRow
                            ? 'absolute left-0 right-0 h-0.5 bg-blue-500 z-10 pointer-events-none'
                            : 'absolute top-0 bottom-0 w-1 bg-blue-500 z-10 pointer-events-none'
                    }
                    style={
                        isRow
                            ? {
                                  top: 0,
                                  transform: `translateY(${insertOffset}px)`,
                                  transition: `transform ${transitionMs}ms ease-out`,
                              }
                            : {
                                  left: 0,
                                  transform: `translateX(${insertOffset}px)`,
                                  transition: `transform ${transitionMs}ms ease-out`,
                              }
                    }
                />
            )}

            {/* Ghost chip */}
            {ghostOffset !== null && ghostLabel && (
                <div
                    className={
                        isRow
                            ? 'absolute -left-8 z-20 max-w-[240px] px-2 py-1 bg-white border border-blue-400 shadow-sm text-xs rounded-md truncate pointer-events-none'
                            : 'absolute -top-8 z-20 max-w-[240px] px-2 py-1 bg-white border border-blue-400 shadow-sm text-xs rounded-md truncate pointer-events-none'
                    }
                    style={
                        isRow
                            ? {
                                  top: 0,
                                  transform: `translateY(${ghostOffset}px) translateY(-50%)`,
                                  transition: `transform ${transitionMs}ms ease-out`,
                              }
                            : {
                                  left: 0,
                                  transform: `translateX(${ghostOffset}px) translateX(-50%)`,
                                  transition: `transform ${transitionMs}ms ease-out`,
                              }
                    }
                >
                    {ghostLabel}
                </div>
            )}
        </>
    );
}
